import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Target, MessageSquare, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SCORE = { HOT:"bg-rose-100 text-rose-800 border-rose-200", HIGH:"bg-orange-100 text-orange-800 border-orange-200", MEDIUM:"bg-amber-100 text-amber-800 border-amber-200", LOW:"bg-slate-100 text-slate-700 border-slate-200" };

export default function QualifiedLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const nav = useNavigate();
  const { user } = useAuth();
  const prefix = user?.role === "sales_associate" ? "/sales" : "";

  const load = async (score) => {
    const r = await api.get("/leads", { params: score ? { score } : {} });
    setLeads(r.data);
  };
  useEffect(() => { load(filter); }, [filter]);

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Target className="w-6 h-6 text-emerald-600"/>Qualified Leads</h1>
        <p className="text-sm text-slate-500">Genuinely interested prospects surfaced by AI qualification</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["", "All"], ["HOT","Hot"], ["HIGH","High"], ["MEDIUM","Medium"], ["LOW","Low"]].map(([v, l]) => (
          <button key={v||"all"} onClick={()=>setFilter(v)}
            data-testid={`filter-${v||"all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filter===v ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {leads.map((l) => (
          <Card key={l.id} className="p-5 border-slate-200 hover:shadow-md transition cursor-pointer"
                onClick={() => nav(`${prefix}/leads/${l.id}`)} data-testid={`qualified-lead-${l.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{l.name}</div>
                <div className="text-xs font-mono text-slate-500 mt-0.5">{l.phone}</div>
              </div>
              <Badge className={SCORE[l.score]}>{l.score === "HOT" && <Flame className="w-3 h-3 mr-1"/>}{l.score}</Badge>
            </div>
            <div className="mt-4 space-y-1.5 text-sm">
              {l.requirement && <div><span className="text-slate-500">Requirement: </span><span className="font-medium">{l.requirement}</span></div>}
              {l.budget && <div><span className="text-slate-500">Budget: </span><span className="font-medium">{l.budget}</span></div>}
              {l.location && <div><span className="text-slate-500">Location: </span><span className="font-medium">{l.location}</span></div>}
              {l.timeline && <div><span className="text-slate-500">Timeline: </span><span className="font-medium">{l.timeline}</span></div>}
            </div>
            {l.ai_summary && (
              <div className="mt-3 p-2 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-950">
                <span className="font-semibold text-indigo-700">AI: </span>{l.ai_summary}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <Badge variant="outline" className="text-[10px]">{l.status}</Badge>
              <Button size="sm" variant="outline" className="ml-auto" onClick={async (e)=>{
                e.stopPropagation();
                const conv = await api.get("/conversations", { params: {} });
                const c = conv.data.find(x => x.contact_id === l.contact_id);
                if (c) nav(`${prefix}/inbox/${c.id}`);
              }} data-testid={`open-conv-${l.id}`}><MessageSquare className="w-3 h-3 mr-1"/>Open chat</Button>
            </div>
          </Card>
        ))}
        {leads.length === 0 && <div className="col-span-full text-center p-12 text-slate-500">No qualified leads yet</div>}
      </div>
    </div>
  );
}
